import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { da } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { selectCls } from "@/components/forms";

export type Line = { kind: string; item_id?: string | null; label: string; qty: number; unit_price: number; tva_rate: number; discount: number; total_ht?: number };

export function computeTotals(lines: Line[]) {
  let ht = 0;
  let tva = 0;
  lines.forEach((l) => {
    const t = Number(l.qty || 0) * Number(l.unit_price || 0) * (1 - Number(l.discount || 0) / 100);
    ht += t;
    tva += (t * Number(l.tva_rate || 0)) / 100;
  });
  return { ht, tva, ttc: ht + tva };
}

export function Totals({ totals, label = "Total à payer" }: { totals: any; label?: string }) {
  if (!Number(totals.tva)) {
    return (
      <div className="ml-auto mt-6 w-full max-w-xs space-y-1.5 text-sm" data-testid="totals-block">
        <div className="flex justify-between border-t border-border pt-2 text-base font-bold"><span>{label}</span><span className="font-mono-data" data-testid="total-ttc">{da(totals.ttc)}</span></div>
        <div className="text-right text-xs text-muted-foreground" data-testid="no-tva-note">Sans TVA</div>
      </div>
    );
  }
  return (
    <div className="ml-auto mt-6 w-full max-w-xs space-y-1.5 text-sm" data-testid="totals-block">
      <div className="flex justify-between"><span className="text-muted-foreground">Total HT</span><span className="font-mono-data" data-testid="total-ht">{da(totals.ht)}</span></div>
      <div className="flex justify-between"><span className="text-muted-foreground">TVA</span><span className="font-mono-data" data-testid="total-tva">{da(totals.tva)}</span></div>
      <div className="flex justify-between border-t border-border pt-2 text-base font-bold"><span>Total TTC</span><span className="font-mono-data" data-testid="total-ttc">{da(totals.ttc)}</span></div>
    </div>
  );
}

export function LinesView({ lines }: { lines: Line[] }) {
  const tva = lines.some((l) => Number(l.tva_rate) > 0);
  const head = tva ? ["Désignation", "Type", "Qté", "P.U. HT", "Rem.", "TVA", "Total HT"] : ["Désignation", "Type", "Qté", "P.U.", "Rem.", "Total"];
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="lines-view">
        <thead><tr className="border-b border-border text-left">{head.map((h) => <th key={h} className="eyebrow px-2 py-2">{h}</th>)}</tr></thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={i} className="border-b border-border/60">
              <td className="px-2 py-2.5">{l.label}</td>
              <td className="px-2 text-xs text-muted-foreground">{l.kind === "part" ? "Pièce" : "M.O."}</td>
              <td className="px-2 font-mono-data">{l.qty}</td>
              <td className="px-2 font-mono-data">{da(l.unit_price)}</td>
              <td className="px-2 font-mono-data">{l.discount || 0} %</td>
              {tva && <td className="px-2 font-mono-data">{l.tva_rate} %</td>}
              <td className="px-2 font-mono-data">{da(l.total_ht ?? l.qty * l.unit_price)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function LinesEditor({ lines, onChange, defaultTva }: { lines: Line[]; onChange: (l: Line[]) => void; defaultTva: number }) {
  const [items, setItems] = useState<any[]>([]);
  const [pick, setPick] = useState("");
  const [packages, setPackages] = useState<any[]>([]);
  useEffect(() => {
    api.get("/items").then((r) => setItems(r.data.filter((i: any) => i.active)));
    api.get("/packages").then((r) => setPackages(r.data.filter((p: any) => p.active)));
  }, []);
  const addPackage = (id: string) => {
    const p = packages.find((x) => x.id === id);
    if (!p) return;
    onChange([...lines, ...p.lines.filter((l: any) => l.item.active).map((l: any) => ({ kind: l.item.kind, item_id: l.item.id, label: l.item.label, qty: l.qty, unit_price: l.item.unit_price, tva_rate: l.item.tva_rate, discount: 0 }))]);
  };
  const upd = (i: number, k: keyof Line, v: any) => onChange(lines.map((l, j) => (j === i ? { ...l, [k]: k === "label" || k === "kind" ? v : Number(v) } : l)));
  const addItem = (id: string) => {
    const it = items.find((x) => x.id === id);
    if (!it) return;
    onChange([...lines, { kind: it.kind, item_id: it.id, label: it.label, qty: 1, unit_price: it.unit_price, tva_rate: it.tva_rate, discount: 0 }]);
    setPick("");
  };
  const addFree = () => onChange([...lines, { kind: "service", item_id: null, label: "", qty: 1, unit_price: 0, tva_rate: defaultTva, discount: 0 }]);
  return (
    <div data-testid="lines-editor">
      <div className="space-y-2">
        {lines.map((l, i) => (
          <div key={i} className="grid grid-cols-12 items-center gap-2 rounded-sm border border-border p-2" data-testid={`line-row-${i}`}>
            <Input className="col-span-12 md:col-span-4" value={l.label} onChange={(e) => upd(i, "label", e.target.value)} placeholder="Désignation" data-testid={`line-label-${i}`} />
            <select className={`${selectCls} col-span-4 md:col-span-2`} value={l.kind} onChange={(e) => upd(i, "kind", e.target.value)} data-testid={`line-kind-${i}`}>
              <option value="service">M.O.</option>
              <option value="part">Pièce</option>
            </select>
            <Input className="col-span-2 md:col-span-1" type="number" value={l.qty} onChange={(e) => upd(i, "qty", e.target.value)} data-testid={`line-qty-${i}`} title="Quantité" />
            <Input className={`col-span-3 md:col-span-2 ${!Number(l.unit_price) ? "border-amber-500/80 bg-amber-500/10" : ""}`} type="number" value={l.unit_price} onChange={(e) => upd(i, "unit_price", e.target.value)} data-testid={`line-price-${i}`} title="Prix unitaire HT — à définir par le garagiste" placeholder="Prix HT" />
            <Input className="col-span-1 px-1 md:col-span-1" type="number" value={l.discount} onChange={(e) => upd(i, "discount", e.target.value)} data-testid={`line-discount-${i}`} title="Remise %" />
            <Input className="col-span-1 px-1 md:col-span-1" type="number" value={l.tva_rate} onChange={(e) => upd(i, "tva_rate", e.target.value)} data-testid={`line-tva-${i}`} title="TVA %" />
            <button className="col-span-1 flex justify-center text-muted-foreground transition-colors hover:text-red-400" onClick={() => onChange(lines.filter((_, j) => j !== i))} data-testid={`line-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <select className={selectCls} value={pick} onChange={(e) => addItem(e.target.value)} data-testid="line-add-catalog-select">
          <option value="">+ Ajouter une prestation ou une pièce du catalogue…</option>
          {Array.from(new Set(items.map((it) => (it.kind === "part" ? "Pièces" : it.category || "Prestations")))).sort((a, b) => a.localeCompare(b, "fr")).map((g) => (
            <optgroup key={g} label={g}>
              {items.filter((it) => (it.kind === "part" ? "Pièces" : it.category || "Prestations") === g).map((it) => (
                <option key={it.id} value={it.id}>{it.label} — {it.unit_price ? da(it.unit_price) : "prix à saisir"}</option>
              ))}
            </optgroup>
          ))}
        </select>
        {packages.length > 0 && (
          <select className={selectCls} value="" onChange={(e) => addPackage(e.target.value)} data-testid="line-add-package-select">
            <option value="">+ Insérer un forfait…</option>
            {packages.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.lines.length} lignes)</option>)}
          </select>
        )}
        <Button variant="outline" onClick={addFree} data-testid="line-add-free-btn"><Plus className="mr-1 h-4 w-4" />Ligne libre</Button>
      </div>
      {lines.some((l) => !Number(l.unit_price)) && <p className="mt-2 text-xs text-amber-400" data-testid="lines-price-missing">Saisissez le prix HT des lignes surlignées : vous fixez librement le montant facturé.</p>}
      <Totals totals={computeTotals(lines)} />
    </div>
  );
}
